import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Zap, Loader2, Plus, X, ExternalLink, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBreakingNews() {
  const { authFetch } = useAdmin();
  const [articles, setArticles] = useState([]);
  const [moduleItems, setModuleItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState([]);
  const [newBanner, setNewBanner] = useState({ title: '', url: '', color: 'red' });

  const load = useCallback(async () => {
    setLoading(true);
    const [a, m] = await Promise.all([
      authFetch(`${API}/admin/breaking-news-articles`).then(r => r.ok ? r.json() : { items: [] }),
      authFetch(`${API}/admin/modules/breaking-news/items?limit=200`).then(r => r.ok ? r.json() : { items: [] }),
    ]);
    setArticles(a.items || []);
    setModuleItems(m.items || []);
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const searchArticles = async () => {
    if (!q.trim()) { setSearch([]); return; }
    const r = await authFetch(`${API}/admin/articles?q=${encodeURIComponent(q)}&limit=20`);
    if (r.ok) {
      const d = await r.json();
      setSearch((d.articles || d.items || []).slice(0, 20));
    }
  };

  const toggleBreaking = async (articleId, on) => {
    const r = await authFetch(`${API}/admin/articles/${articleId}/toggle-breaking`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_breaking: on }),
    });
    if (r.ok) { toast.success(on ? 'Označeno kao breaking' : 'Uklonjeno'); load(); setSearch([]); setQ(''); }
  };

  const createBanner = async () => {
    if (!newBanner.title) return toast.error('Naslov je obavezan');
    const r = await authFetch(`${API}/admin/modules/breaking-news/items`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: newBanner.title, data: { url: newBanner.url, color: newBanner.color } }),
    });
    if (r.ok) { toast.success('Dodano'); setNewBanner({ title: '', url: '', color: 'red' }); load(); }
  };

  const deleteBanner = async (id) => {
    await authFetch(`${API}/admin/modules/breaking-news/items/${id}`, { method: 'DELETE' });
    load();
  };

  return (
    <div className="p-4 md:p-6" data-testid="admin-breaking-news">
      <div className="flex items-center gap-3 mb-5">
        <Zap className="text-red-500" size={24} />
        <div>
          <h1 className="text-2xl font-bold">Vijesti u prekidu</h1>
          <p className="text-sm text-zinc-400">Članci označeni kao <code>is_breaking</code> + ručne vrpce u headeru.</p>
        </div>
      </div>

      {/* Section 1: Breaking articles */}
      <section className="mb-8">
        <h2 className="text-sm uppercase font-bold tracking-wider text-zinc-300 mb-3">Članci označeni kao breaking</h2>
        {loading ? <Loader2 className="animate-spin text-zinc-500" /> : articles.length === 0 ? (
          <p className="text-sm text-zinc-500">Nema članaka označenih kao breaking.</p>
        ) : (
          <ul className="space-y-2">
            {articles.map(a => (
              <li key={a.id} className="flex items-center gap-3 p-3 rounded border border-zinc-800 bg-zinc-900/50" data-testid={`br-art-${a.id}`}>
                {a.cover_image_url ? (
                  <img src={a.cover_image_url} alt="" className="w-14 h-10 rounded object-cover" />
                ) : <div className="w-14 h-10 rounded bg-red-600/40" />}
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{a.title_hr}</p>
                  <p className="text-xs text-zinc-500">{(a.publish_at || '').slice(0, 16)} · <span className={a.status === 'published' ? 'text-emerald-400' : 'text-amber-400'}>{a.status}</span></p>
                </div>
                <Link to={`/admin/articles/${a.id}/edit`} className="text-xs text-blue-400 hover:underline">Uredi</Link>
                <a href={`/clanak/${a.slug}`} target="_blank" rel="noreferrer" className="text-xs text-zinc-400"><ExternalLink size={12} /></a>
                <button onClick={() => toggleBreaking(a.id, false)} data-testid={`unmark-${a.id}`}
                        className="px-2 py-1 text-xs bg-amber-600/80 hover:bg-amber-600 rounded">Ukloni breaking</button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 p-3 rounded-lg border border-dashed border-zinc-800">
          <p className="text-xs text-zinc-400 mb-2">Dodaj postojeći članak kao breaking:</p>
          <div className="flex gap-2 mb-2">
            <div className="relative flex-1">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchArticles()}
                     placeholder="Traži naslov članka…" data-testid="br-search"
                     className="w-full pl-7 pr-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm" />
            </div>
            <button onClick={searchArticles} className="px-3 py-1.5 bg-zinc-800 rounded text-sm">Traži</button>
          </div>
          {search.map(a => (
            <div key={a.id} className="flex items-center gap-2 py-1 text-xs">
              <span className="flex-1 truncate">{a.title_hr}</span>
              <button onClick={() => toggleBreaking(a.id, true)} data-testid={`mark-${a.id}`}
                      className="px-2 py-0.5 bg-red-600 hover:bg-red-700 rounded">Označi breaking</button>
            </div>
          ))}
        </div>
      </section>

      {/* Section 2: Manual banners */}
      <section>
        <h2 className="text-sm uppercase font-bold tracking-wider text-zinc-300 mb-3">Ručne breaking vrpce (header)</h2>
        <div className="flex flex-wrap gap-2 mb-3">
          <input value={newBanner.title} onChange={e => setNewBanner({ ...newBanner, title: e.target.value })}
                 placeholder="Tekst vrpce" data-testid="banner-title"
                 className="flex-1 min-w-[200px] px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm" />
          <input value={newBanner.url} onChange={e => setNewBanner({ ...newBanner, url: e.target.value })}
                 placeholder="Link (izborno)" data-testid="banner-url"
                 className="flex-1 min-w-[200px] px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm" />
          <select value={newBanner.color} onChange={e => setNewBanner({ ...newBanner, color: e.target.value })}
                  className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm">
            <option value="red">Crvena</option>
            <option value="amber">Žuta</option>
            <option value="blue">Plava</option>
          </select>
          <button onClick={createBanner} data-testid="banner-add"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-sm font-bold">
            <Plus size={12} /> Dodaj
          </button>
        </div>

        <ul className="space-y-1">
          {moduleItems.map(i => (
            <li key={i.id} className={`flex items-center gap-3 p-2 rounded ${
              (i.data?.color === 'amber') ? 'bg-amber-900/30' : (i.data?.color === 'blue') ? 'bg-blue-900/30' : 'bg-red-900/30'
            }`} data-testid={`banner-${i.id}`}>
              <Zap size={12} />
              <span className="flex-1">{i.title}</span>
              {i.data?.url && <a href={i.data.url} target="_blank" rel="noreferrer" className="text-xs underline">link</a>}
              <button onClick={() => deleteBanner(i.id)} className="p-1 text-red-300 hover:text-red-100" data-testid={`banner-del-${i.id}`}>
                <X size={12} />
              </button>
            </li>
          ))}
          {moduleItems.length === 0 && <li className="text-xs text-zinc-500 py-2">Nema ručnih vrpci.</li>}
        </ul>
      </section>
    </div>
  );
}
